import { FC, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogContent,
    DialogActions,
    makeStyles,
    shorthands,
    tokens,
    Text,
    Input,
} from '@fluentui/react-components';
import SyButton from '../Common/SyButton';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { ProblemDetails } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';

export interface SurveyDuplicateDialogProps {
    open: boolean;
    sourceSurveyId?: string | null;
    sourceSurveyName?: string | null;
    onClose: () => void;
    onSuccess?: () => void; // called when duplication succeeded
}

const useStyles = makeStyles({
    dialogSurface: {
        maxWidth: '520px',
        ...shorthands.borderRadius('12px'),
        ...shorthands.overflow('hidden'),
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.14), 0 0 2px rgba(0, 0, 0, 0.12)',
        ...shorthands.padding(0),
    },
    dialogBody: {
        ...shorthands.padding(0),
        display: 'flex',
        flexDirection: 'column',
    },
    dialogContent: {
        ...shorthands.padding('20px'),
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    dialogActions: {
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.padding('12px', '16px'),
        ...shorthands.gap('8px'),
        ...shorthands.margin(0),
        width: '100%',
        justifyContent: 'flex-end',
        display: 'flex',
        flexDirection: 'row',
    },
    label: {
        fontSize: '13px',
        color: tokens.colorNeutralForeground3,
    },
});

const SurveyDuplicateDialog: FC<SurveyDuplicateDialogProps> = ({ open, sourceSurveyId, sourceSurveyName, onClose, onSuccess }) => {
    const styles = useStyles();
    const [name, setName] = useState<string>(sourceSurveyName || '');
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(sourceSurveyName || '');
            setError(null);
        }
    }, [open, sourceSurveyName]);

    const handleDuplicate = async () => {
        if (!name || name.trim().length === 0) {
            setError('Please enter a name for the duplicated survey.');
            return;
        }
        if (!sourceSurveyId) {
            setError('Source survey is not specified.');
            return;
        }

        setIsSaving(true);
        const abortController = new AbortController();

        try {
            await performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    const result = await client.duplicateSurvey(sourceSurveyId!, name.trim(), signal);

                    // If server returns boolean false (forbidden duplicate name), surface that to the user
                    if (result === false) {
                        setError('The server refused to duplicate the survey. The name might already exist.');
                        return;
                    }

                    // Successful duplication
                    if (onSuccess) onSuccess();
                    onClose();
                } catch (error) {
                    console.error('Failed to duplicate survey (server error):', error);
                    // If server returned ProblemDetails (e.g. 409 Conflict), show its detail property if available
                    if (error instanceof ProblemDetails && error.detail) {
                        setError(error.detail);
                    } else if (error && typeof (error as Error).message === 'string') {
                        setError((error as Error).message);
                    } else {
                        setError('Failed to duplicate survey. Please try again.');
                    }
                }
            }, { abortController });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open}>
            <DialogSurface className={styles.dialogSurface}>
                <DialogBody className={styles.dialogBody}>
                    <DialogContent className={styles.dialogContent}>
                        <div>
                            <Text weight="semibold">Duplicate survey</Text>
                        </div>
                        <div>
                            <Text className={styles.label}>New survey name</Text>
                            <Input
                                placeholder="Enter new survey name"
                                value={name}
                                onChange={(e) => { setName((e.target as HTMLInputElement).value); if (error) setError(null); }}
                                aria-label="New survey name"
                            />
                            {error && (
                                <div style={{ color: '#d13438', marginTop: 8 }}>
                                    {error}
                                </div>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions className={styles.dialogActions}>
                        <SyButton appearance="tertiary" onClick={onClose} disabled={isSaving}>
                            Cancel
                        </SyButton>
                        <SyButton appearance="primary" onClick={handleDuplicate} disabled={isSaving}>
                            Duplicate
                        </SyButton>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default SurveyDuplicateDialog;
